import {
	createDefaultInventory,
	DEFAULT_BEST_OF,
	DEFAULT_PLAYER_NAMES,
	MAX_WIND,
	PLAYER_COLORS,
	SCORCHED_EARTH_CONFIG,
	TANK_HEIGHT,
	TANK_WIDTH,
	TICK_MS,
	TURN_TIMER_TICKS,
	WORLD_HEIGHT,
	WORLD_WIDTH,
	WEAPONS,
} from './scorched-earth.constants';
import {
	clampAngle,
	clampPower,
	createMirvWarheads,
	damageAtDistance,
	distance,
	launchVelocity,
	projectileHitsTerrain,
	projectileOutOfBounds,
	shouldSplitMirv,
	stepProjectile,
} from './scorched-earth.physics';
import { hashSeed } from './scorched-earth.rng';
import { carveCrater, generateTerrain, placeTankOnTerrain } from './scorched-earth.terrain';
import { WEAPON_VISUALS } from './scorched-earth.visuals';
import {
	AimInput,
	CombatantConfig,
	CombatantKind,
	CpuDifficulty,
	ExplosionFx,
	NapalmZone,
	Projectile,
	ScorchedEarthState,
	Tank,
	WeaponId,
} from './scorched-earth.types';

export function createInitialState(seed = Date.now(), playerCount = 2): ScorchedEarthState {
	const count = clamp(playerCount, 2, 4);
	const terrain = generateTerrain(seed);
	const players = createPlayers(count, terrain, seed);
	return {
		seed,
		phase: 'setup',
		terrain,
		wind: rollWind(seed),
		players,
		currentPlayerIndex: 0,
		selectedWeapon: 'missile',
		projectiles: [],
		explosions: [],
		napalmZones: [],
		round: 1,
		bestOf: DEFAULT_BEST_OF,
		winnerId: null,
		matchWinnerId: null,
		turnTimerTicks: TURN_TIMER_TICKS,
		turnTimerRemaining: TURN_TIMER_TICKS,
		lastShopResult: null,
		message: 'Configure combatants and start the battle.',
		nextProjectileId: 1,
		nextFxId: 1,
	};
}

export function startRound(state: ScorchedEarthState): ScorchedEarthState {
	const alive = state.players.filter((p) => p.alive);
	if (alive.length < 2) {
		return resolveMatchIfNeeded({ ...state, phase: 'gameOver' });
	}
	const nextIndex = findNextAliveIndex(state.players, -1);
	return withTurnTimer(
		turnMessageForPlayer({
			...state,
			phase: 'aiming',
			currentPlayerIndex: nextIndex,
			selectedWeapon: pickDefaultWeapon(state.players[nextIndex]),
			projectiles: [],
			explosions: [],
			wind: rollWind(state.seed + state.round * 997),
		}),
	);
}

export function beginGame(state: ScorchedEarthState): ScorchedEarthState {
	const terrain = generateTerrain(state.seed);
	const spawnXs = computeSpawnPositions(state.players.length, terrain, state.seed);
	const players = state.players.map((p, i) =>
		placeTankAt(repositionTank(p, terrain, spawnXs[i]), terrain, spawnXs[i]),
	);
	return startRound({
		...state,
		terrain,
		players: players.map((p) => ({ ...p, alive: true, health: p.maxHealth })),
		round: 1,
		winnerId: null,
		matchWinnerId: null,
		lastShopResult: null,
	});
}

export function enterShopPhase(state: ScorchedEarthState): ScorchedEarthState {
	const players = state.players.map((p) => ({
		...p,
		credits: p.credits + (p.alive ? SCORCHED_EARTH_CONFIG.roundShopCredits : 0),
	}));
	return {
		...state,
		phase: 'shop',
		players,
		lastShopResult: null,
		message: `Shop open — each survivor receives ${SCORCHED_EARTH_CONFIG.roundShopCredits} credits.`,
	};
}

export function buyWeapon(state: ScorchedEarthState, playerId: number, weaponId: WeaponId): ScorchedEarthState {
	const player = state.players.find((p) => p.id === playerId);
	if (!player) {
		return state;
	}
	const weapon = WEAPONS[weaponId];
	if (!player.alive) {
		return {
			...state,
			lastShopResult: {
				playerId,
				weaponId,
				success: false,
				message: `${player.name} cannot buy while eliminated.`,
			},
			message: `${player.name} cannot buy while eliminated.`,
		};
	}
	if (player.credits < weapon.shopCost) {
		return {
			...state,
			lastShopResult: {
				playerId,
				weaponId,
				success: false,
				message: `${player.name} cannot afford ${weapon.label} (${weapon.shopCost} cr).`,
			},
			message: `${player.name} cannot afford ${weapon.label} (${weapon.shopCost} cr).`,
		};
	}

	const players = state.players.map((p) => {
		if (p.id !== playerId) {
			return p;
		}
		return {
			...p,
			credits: p.credits - weapon.shopCost,
			inventory: { ...p.inventory, [weaponId]: p.inventory[weaponId] + 1 },
		};
	});
	return {
		...state,
		players,
		lastShopResult: {
			playerId,
			weaponId,
			success: true,
			message: `${player.name} bought ${weapon.label} for ${weapon.shopCost} cr.`,
		},
		message: `${player.name} bought ${weapon.label} for ${weapon.shopCost} cr.`,
	};
}

export function finishShop(state: ScorchedEarthState): ScorchedEarthState {
	const seed = hashSeed([state.seed, state.round + 1]);
	const terrain = generateTerrain(seed);
	const spawnXs = computeSpawnPositions(state.players.length, terrain, seed);
	// Every match combatant returns for the next round; inventory/credits/wins persist.
	const players = state.players.map((p, i) =>
		placeTankAt(
			repositionTank({ ...p, alive: true, health: p.maxHealth }, terrain, spawnXs[i]),
			terrain,
			spawnXs[i],
		),
	);
	return startRound({
		...state,
		seed,
		terrain,
		players,
		round: state.round + 1,
		winnerId: null,
		lastShopResult: null,
	});
}

export function updateAim(state: ScorchedEarthState, partial: Partial<AimInput>): ScorchedEarthState {
	if (state.phase !== 'aiming' && state.phase !== 'shop') {
		return state;
	}
	const players = [...state.players];
	const idx = state.currentPlayerIndex;
	const current = players[idx];
	if (!current?.alive) {
		return state;
	}
	players[idx] = {
		...current,
		angle: partial.angle !== undefined ? clampAngle(partial.angle) : current.angle,
		power: partial.power !== undefined ? clampPower(partial.power) : current.power,
	};
	const selectedWeapon =
		partial.weaponId !== undefined && current.inventory[partial.weaponId] > 0
			? partial.weaponId
			: state.selectedWeapon;
	return { ...state, players, selectedWeapon };
}

export function fireWeapon(state: ScorchedEarthState): ScorchedEarthState {
	if (state.phase !== 'aiming') {
		return state;
	}
	const player = state.players[state.currentPlayerIndex];
	if (!player?.alive) {
		return state;
	}
	const weaponId = state.selectedWeapon;
	if (player.inventory[weaponId] <= 0) {
		return { ...state, message: `${player.name} is out of ammo for ${WEAPONS[weaponId].label}.` };
	}

	const facingRight = player.x < WORLD_WIDTH / 2;
	const vel = launchVelocity(player.angle, player.power, facingRight, weaponId);
	const startY = player.y - TANK_HEIGHT;
	const projectile: Projectile = {
		id: state.nextProjectileId,
		x: player.x,
		y: startY,
		vx: vel.x,
		vy: vel.y,
		ownerId: player.id,
		weaponId,
		age: 0,
		splitDone: weaponId !== 'mirv',
		trail: [],
	};

	const players = state.players.map((p) =>
		p.id === player.id
			? { ...p, inventory: { ...p.inventory, [weaponId]: p.inventory[weaponId] - 1 } }
			: p,
	);

	return {
		...state,
		phase: 'projectile',
		players,
		projectiles: [projectile],
		nextProjectileId: state.nextProjectileId + 1,
		turnTimerRemaining: 0,
		message: `${player.name} fired ${WEAPONS[weaponId].label}.`,
	};
}

export function tickProjectiles(state: ScorchedEarthState): ScorchedEarthState {
	if (state.phase !== 'projectile') {
		return state;
	}

	let projectiles = [...state.projectiles];
	let explosions = [...state.explosions];
	let napalmZones = [...state.napalmZones];
	let terrain = state.terrain;
	let players = [...state.players];
	let nextProjectileId = state.nextProjectileId;
	let nextFxId = state.nextFxId;
	const stillFlying: Projectile[] = [];

	for (const p of projectiles) {
		let current = stepProjectile(p, terrain, state.wind);
		if (shouldSplitMirv(current)) {
			const split = createMirvWarheads(current, nextProjectileId);
			nextProjectileId = split.nextId;
			stillFlying.push(...split.projectiles);
			current = { ...current, splitDone: true, vx: current.vx * 0.2, vy: current.vy * 0.2 };
		}

		if (projectileHitsTerrain(current, terrain) || projectileOutOfBounds(current, WORLD_WIDTH, WORLD_HEIGHT)) {
			const impact = resolveImpact(current, terrain, players, explosions, napalmZones, nextFxId);
			terrain = impact.terrain;
			players = impact.players;
			explosions = impact.explosions;
			napalmZones = impact.napalmZones;
			nextFxId = impact.nextFxId;
		} else {
			stillFlying.push(current);
		}
	}

	projectiles = stillFlying;
	explosions = ageExplosions(explosions);
	const napalmTick = advanceNapalm(players, napalmZones, terrain);
	players = napalmTick.players;
	napalmZones = napalmTick.zones;

	if (projectiles.length === 0 && explosions.every((e) => e.age >= e.maxAge)) {
		return endTurn({
			...state,
			terrain,
			players,
			explosions,
			napalmZones,
			projectiles,
			nextProjectileId,
			nextFxId,
		});
	}

	return {
		...state,
		terrain,
		players,
		projectiles,
		explosions,
		napalmZones,
		nextProjectileId,
		nextFxId,
	};
}

function resolveImpact(
	projectile: Projectile,
	terrain: Float32Array,
	players: Tank[],
	explosions: ExplosionFx[],
	napalmZones: NapalmZone[],
	nextFxId: number,
): {
	terrain: Float32Array;
	players: Tank[];
	explosions: ExplosionFx[];
	napalmZones: NapalmZone[];
	nextFxId: number;
} {
	const weapon = WEAPONS[projectile.weaponId];
	const nextTerrain = terrain.slice();
	carveCrater(nextTerrain, projectile.x, projectile.y, weapon.blastRadius);

	const explosion: ExplosionFx = {
		id: nextFxId++,
		x: projectile.x,
		y: projectile.y,
		radius: weapon.blastRadius,
		weaponId: projectile.weaponId,
		age: 0,
		maxAge: WEAPON_VISUALS[projectile.weaponId].impactMaxAge,
	};
	explosions = [...explosions, explosion];

	const nextPlayers = players.map((tank) => {
		if (!tank.alive) {
			return tank;
		}
		const dist = distance({ x: projectile.x, y: projectile.y }, { x: tank.x, y: tank.y - TANK_HEIGHT / 2 });
		const dmg = damageAtDistance(weapon.maxDamage, weapon.blastRadius, dist);
		const health = Math.max(0, tank.health - dmg);
		const surfaceY = placeTankOnTerrain(nextTerrain, tank.x);
		const buried = surfaceY > tank.y + 2;
		const fallDamage = buried ? 12 : 0;
		const finalHealth = Math.max(0, health - fallDamage);
		const wasAlive = tank.alive;
		const nowAlive = finalHealth > 0;
		return {
			...tank,
			y: Math.max(tank.y, surfaceY),
			health: finalHealth,
			alive: nowAlive,
			credits:
				wasAlive && !nowAlive && tank.id !== projectile.ownerId
					? tank.credits
					: tank.credits,
		};
	});

	let nextNapalm = napalmZones;
	if (projectile.weaponId === 'napalm' && weapon.napalmRadius) {
		nextNapalm = [
			...napalmZones,
			{
				id: nextFxId++,
				x: projectile.x,
				y: projectile.y,
				radius: weapon.napalmRadius,
				remainingTicks: weapon.napalmDurationTicks ?? 120,
				tickDamage: weapon.napalmTickDamage ?? 3,
				ownerId: projectile.ownerId,
			},
		];
	}

	return {
		terrain: nextTerrain,
		players: nextPlayers,
		explosions,
		napalmZones: nextNapalm,
		nextFxId,
	};
}

export function tickNapalmEffects(state: ScorchedEarthState): ScorchedEarthState {
	if (!state.napalmZones.length) {
		return state;
	}
	const napalmTick = advanceNapalm(state.players, state.napalmZones, state.terrain);
	return {
		...state,
		players: settlePlayersOnTerrain(napalmTick.players, state.terrain),
		napalmZones: napalmTick.zones,
	};
}

function advanceNapalm(
	players: Tank[],
	zones: NapalmZone[],
	terrain: Float32Array,
): { players: Tank[]; zones: NapalmZone[] } {
	if (!zones.length) {
		return { players, zones };
	}
	const remaining: NapalmZone[] = [];
	let nextPlayers = players;
	for (const zone of zones) {
		if (zone.remainingTicks <= 1) {
			continue;
		}
		nextPlayers = nextPlayers.map((tank) => {
			if (!tank.alive) {
				return tank;
			}
			const dist = distance({ x: zone.x, y: zone.y }, { x: tank.x, y: tank.y });
			if (dist <= zone.radius && zone.remainingTicks % 20 === 0) {
				const health = Math.max(0, tank.health - zone.tickDamage);
				return { ...tank, health, alive: health > 0 };
			}
			return tank;
		});
		remaining.push({ ...zone, remainingTicks: zone.remainingTicks - 1 });
	}
	return { players: nextPlayers, zones: remaining };
}

function settlePlayersOnTerrain(players: Tank[], terrain: Float32Array): Tank[] {
	return players.map((tank) => {
		if (!tank.alive) {
			return tank;
		}
		const surfaceY = placeTankOnTerrain(terrain, tank.x);
		return { ...tank, y: Math.max(tank.y, surfaceY) };
	});
}

function ageExplosions(explosions: ExplosionFx[]): ExplosionFx[] {
	return explosions
		.map((e) => ({ ...e, age: e.age + 1 }))
		.filter((e) => e.age < e.maxAge);
}

function endTurn(state: ScorchedEarthState): ScorchedEarthState {
	const alive = state.players.filter((p) => p.alive);
	if (alive.length <= 1) {
		const winner = alive[0] ?? null;
		const killCredits = SCORCHED_EARTH_CONFIG.killBonusCredits;
		const winCredits = SCORCHED_EARTH_CONFIG.roundWinCredits;
		const eliminatedCount = winner
			? state.players.filter((p) => !p.alive && p.id !== winner.id).length
			: 0;
		const nextRoundWins = winner ? winner.roundWins + 1 : 0;
		const players = state.players.map((p) => {
			if (!winner || p.id !== winner.id) {
				return p;
			}
			return {
				...p,
				roundWins: nextRoundWins,
				credits: p.credits + winCredits + killCredits * eliminatedCount,
			};
		});
		const roundMessage = winner
			? `${winner.name} wins round ${state.round}! (+${winCredits} cr, match ${nextRoundWins}/${winsNeeded(state.bestOf)})`
			: 'All tanks destroyed — draw.';
		const roundState: ScorchedEarthState = {
			...state,
			phase: 'roundEnd',
			winnerId: winner?.id ?? null,
			players,
			message: roundMessage,
		};
		return resolveMatchIfNeeded(roundState);
	}
	if (everyAliveOutOfAmmo(state.players)) {
		// No survivor can act — end the round as a stalemate so the shop opens and
		// everyone can rearm, instead of bouncing the turn between empty tanks forever.
		return {
			...state,
			phase: 'roundEnd',
			winnerId: null,
			message: 'Stalemate — every tank is out of ammo. Open the shop to rearm.',
		};
	}
	const nextIndex = findNextAliveIndex(state.players, state.currentPlayerIndex);
	const nextPlayer = state.players[nextIndex];
	return withTurnTimer(
		turnMessageForPlayer({
			...state,
			phase: 'aiming',
			currentPlayerIndex: nextIndex,
			selectedWeapon: pickDefaultWeapon(nextPlayer),
		}),
	);
}

export function totalAmmo(player: Tank): number {
	return (
		player.inventory.babyMissile +
		player.inventory.missile +
		player.inventory.babyNuke +
		player.inventory.nuke +
		player.inventory.mirv +
		player.inventory.napalm
	);
}

function everyAliveOutOfAmmo(players: Tank[]): boolean {
	const alive = players.filter((p) => p.alive);
	return alive.length > 0 && alive.every((p) => totalAmmo(p) === 0);
}

export function passTurn(state: ScorchedEarthState): ScorchedEarthState {
	if (state.phase !== 'aiming') {
		return state;
	}
	const player = state.players[state.currentPlayerIndex];
	if (!player?.alive || totalAmmo(player) > 0) {
		return state;
	}
	return endTurn({
		...state,
		message: `${player.name} passes — no ammo remaining.`,
	});
}

export function tickTurnTimer(state: ScorchedEarthState): ScorchedEarthState {
	if (state.phase !== 'aiming' || state.turnTimerRemaining <= 0) {
		return state;
	}
	const remaining = state.turnTimerRemaining - 1;
	if (remaining > 0) {
		return { ...state, turnTimerRemaining: remaining };
	}
	const player = state.players[state.currentPlayerIndex];
	if (!player?.alive) {
		return endTurn({ ...state, turnTimerRemaining: 0 });
	}
	const timeoutMessage =
		totalAmmo(player) === 0
			? `${player.name} ran out of time with no ammo — turn passed.`
			: `${player.name} ran out of time — turn passed.`;
	const next = endTurn({ ...state, turnTimerRemaining: 0 });
	return {
		...next,
		message: `${timeoutMessage} ${next.message}`,
	};
}

export function configureCombatants(
	state: ScorchedEarthState,
	combatants: CombatantConfig[],
): ScorchedEarthState {
	const count = clamp(combatants.length, SCORCHED_EARTH_CONFIG.minPlayers, SCORCHED_EARTH_CONFIG.maxPlayers);
	const terrain = state.terrain.length ? state.terrain : generateTerrain(state.seed);
	const spawnXs = computeSpawnPositions(count, terrain, state.seed);
	const players = combatants.slice(0, count).map((cfg, i) => {
		const existing = state.players[i];
		const x = spawnXs[i];
		return {
			id: i,
			name: cfg.name.trim().slice(0, 12) || DEFAULT_PLAYER_NAMES[i] || `Tank ${i + 1}`,
			color: cfg.color || PLAYER_COLORS[i % PLAYER_COLORS.length],
			kind: cfg.kind,
			difficulty: cfg.kind === 'cpu' ? cfg.difficulty ?? 'normal' : null,
			roundWins: existing?.roundWins ?? 0,
			x,
			y: placeTankOnTerrain(terrain, x),
			angle: existing?.angle ?? 45 + ((state.seed + i * 13) % 20),
			power: existing?.power ?? 55,
			health: existing?.health ?? 100,
			maxHealth: 100,
			alive: true,
			inventory: existing?.inventory ?? createDefaultInventory(),
			credits: existing?.credits ?? 60,
		};
	});
	return { ...state, players, phase: 'setup', message: 'Configure combatants and start the battle.' };
}

export function setPlayerCount(state: ScorchedEarthState, count: number): ScorchedEarthState {
	const nextCount = clamp(count, 2, 4);
	const combatants: CombatantConfig[] = Array.from({ length: nextCount }, (_, i) => {
		const existing = state.players[i];
		return {
			kind: existing?.kind ?? 'human',
			name: existing?.name ?? DEFAULT_PLAYER_NAMES[i] ?? `Tank ${i + 1}`,
			color: existing?.color ?? PLAYER_COLORS[i % PLAYER_COLORS.length],
			difficulty: existing?.difficulty ?? 'normal',
		};
	});
	return configureCombatants(state, combatants);
}

export function setCombatantKind(
	state: ScorchedEarthState,
	playerId: number,
	kind: CombatantKind,
): ScorchedEarthState {
	const players = state.players.map((p) =>
		p.id === playerId
			? {
				...p,
				kind,
				difficulty: kind === 'cpu' ? p.difficulty ?? 'normal' : null,
			}
			: p,
	);
	return { ...state, players };
}

export function setCpuDifficulty(
	state: ScorchedEarthState,
	playerId: number,
	difficulty: CpuDifficulty,
): ScorchedEarthState {
	const players = state.players.map((p) =>
		p.id === playerId && p.kind === 'cpu' ? { ...p, difficulty } : p,
	);
	return { ...state, players };
}

export function setPlayerColor(state: ScorchedEarthState, playerId: number, color: string): ScorchedEarthState {
	const trimmed = color.trim();
	if (!trimmed) {
		return state;
	}
	const players = state.players.map((p) => (p.id === playerId ? { ...p, color: trimmed } : p));
	return { ...state, players };
}

export function setBestOf(state: ScorchedEarthState, bestOf: number): ScorchedEarthState {
	const normalized = clamp(bestOf, 1, 7);
	const odd = normalized % 2 === 0 ? normalized + 1 : normalized;
	return { ...state, bestOf: odd };
}

export function setPlayerName(state: ScorchedEarthState, playerId: number, name: string): ScorchedEarthState {
	const players = state.players.map((p) =>
		p.id === playerId ? { ...p, name: name.trim().slice(0, 12) || p.name } : p,
	);
	return { ...state, players };
}

export function restartGame(state: ScorchedEarthState): ScorchedEarthState {
	const seed = hashSeed([state.seed, Date.now() & 0xffff]);
	const saved = state.players.map((p) => ({
		name: p.name,
		color: p.color,
		kind: p.kind,
		difficulty: p.difficulty,
	}));
	const fresh = createInitialState(seed, state.players.length);
	const combatants: CombatantConfig[] = saved.map((p, i) => ({
		kind: p.kind,
		name: p.name,
		color: p.color,
		difficulty: p.difficulty ?? 'normal',
	}));
	return {
		...configureCombatants({ ...fresh, bestOf: state.bestOf }, combatants),
		turnTimerTicks: state.turnTimerTicks,
	};
}

function createPlayers(count: number, terrain: Float32Array, seed: number): Tank[] {
	const spawnXs = computeSpawnPositions(count, terrain, seed);
	return spawnXs.map((x, i) => ({
		id: i,
		name: DEFAULT_PLAYER_NAMES[i] ?? `Tank ${i + 1}`,
		color: PLAYER_COLORS[i % PLAYER_COLORS.length],
		kind: i === 1 ? 'cpu' as const : 'human' as const,
		difficulty: i === 1 ? 'normal' as const : null,
		roundWins: 0,
		x,
		y: placeTankOnTerrain(terrain, x),
		angle: 45 + ((seed + i * 13) % 20),
		power: 55,
		health: 100,
		maxHealth: 100,
		alive: true,
		inventory: createDefaultInventory(),
		credits: 60,
	}));
}

function repositionTank(tank: Tank, terrain: Float32Array, x: number): Tank {
	return {
		...tank,
		x,
		y: placeTankOnTerrain(terrain, x),
		alive: tank.health > 0,
	};
}

function placeTankAt(tank: Tank, terrain: Float32Array, x: number): Tank {
	return {
		...tank,
		x,
		y: placeTankOnTerrain(terrain, x),
	};
}

function computeSpawnPositions(count: number, terrain: Float32Array, seed: number): number[] {
	const margin = 48;
	const minGap = TANK_WIDTH * 2.5;
	const usable = WORLD_WIDTH - margin * 2;
	const spacing = usable / (count + 1);
	const xs = Array.from({ length: count }, (_, i) => margin + spacing * (i + 1));

	for (let i = 1; i < xs.length; i++) {
		if (xs[i] - xs[i - 1] < minGap) {
			xs[i] = xs[i - 1] + minGap;
		}
	}
	if (xs[xs.length - 1] > WORLD_WIDTH - margin) {
		const overflow = xs[xs.length - 1] - (WORLD_WIDTH - margin);
		for (let i = 0; i < xs.length; i++) {
			xs[i] -= overflow;
		}
	}

	for (let pass = 0; pass < 3; pass++) {
		for (let i = 0; i < xs.length; i++) {
			for (let j = i + 1; j < xs.length; j++) {
				if (xs[j] - xs[i] < minGap) {
					xs[j] = xs[i] + minGap;
				}
			}
		}
	}

	const jitter = ((seed % 11) - 5) * 2;
	return xs.map((x, i) => clamp(x + (i % 2 === 0 ? jitter : -jitter), margin, WORLD_WIDTH - margin));
}

function findNextAliveIndex(players: Tank[], fromIndex: number): number {
	const len = players.length;
	for (let step = 1; step <= len; step++) {
		const idx = (fromIndex + step) % len;
		if (players[idx].alive) {
			return idx;
		}
	}
	return 0;
}

function turnMessageForPlayer(state: ScorchedEarthState): ScorchedEarthState {
	const player = state.players[state.currentPlayerIndex];
	if (!player) {
		return state;
	}
	const outOfAmmo = totalAmmo(player) === 0;
	const timerSec = Math.max(1, Math.ceil((state.turnTimerRemaining * TICK_MS) / 1000));
	const kindLabel = player.kind === 'cpu' ? 'CPU' : 'Human';
	return {
		...state,
		message: outOfAmmo
			? `${player.name}'s turn (${kindLabel}) — out of ammo, pass turn (P).`
			: `${player.name}'s turn (${kindLabel}) — ${timerSec}s remaining.`,
	};
}

function withTurnTimer(state: ScorchedEarthState): ScorchedEarthState {
	const ticks = state.turnTimerTicks || TURN_TIMER_TICKS;
	return { ...state, turnTimerTicks: ticks, turnTimerRemaining: ticks };
}

function winsNeeded(bestOf: number): number {
	return Math.floor(bestOf / 2) + 1;
}

function resolveMatchIfNeeded(state: ScorchedEarthState): ScorchedEarthState {
	if (state.phase !== 'roundEnd' || state.winnerId === null) {
		if (state.phase === 'gameOver' && state.players.filter((p) => p.alive).length < 2) {
			return {
				...state,
				message: state.message || 'Match ended.',
			};
		}
		return state;
	}
	const winner = state.players.find((p) => p.id === state.winnerId);
	if (!winner) {
		return state;
	}
	if (winner.roundWins >= winsNeeded(state.bestOf)) {
		return {
			...state,
			phase: 'gameOver',
			matchWinnerId: winner.id,
			message: `${winner.name} wins the match ${winner.roundWins}-${Math.max(...state.players.filter((p) => p.id !== winner.id).map((p) => p.roundWins), 0)}!`,
		};
	}
	return state;
}

function pickDefaultWeapon(player: Tank): WeaponId {
	const order: WeaponId[] = ['missile', 'babyMissile', 'babyNuke', 'mirv', 'napalm', 'nuke'];
	for (const id of order) {
		if (player.inventory[id] > 0) {
			return id;
		}
	}
	return 'babyMissile';
}

function rollWind(seed: number): number {
	const v = Math.sin(seed * 0.001) * MAX_WIND + Math.cos(seed * 0.0023) * (MAX_WIND * 0.4);
	return Math.round(clamp(v, -MAX_WIND, MAX_WIND));
}

function clamp(v: number, min: number, max: number): number {
	return Math.max(min, Math.min(max, v));
}

export function advanceProjectilePhase(state: ScorchedEarthState, maxTicks = 2000): ScorchedEarthState {
	let next = state;
	for (let i = 0; i < maxTicks && next.phase === 'projectile'; i++) {
		next = tickProjectiles(next);
	}
	return next;
}

export function getCurrentPlayer(state: ScorchedEarthState): Tank | null {
	return state.players[state.currentPlayerIndex] ?? null;
}

export function aliveCount(state: ScorchedEarthState): number {
	return state.players.filter((p) => p.alive).length;
}
